import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { v4 } from 'uuid';
import { Link } from 'react-router-dom';
import { GridCustomColDef } from '../../types';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { formatPercent, formatPrice, getDate, getFormattedDate, chipStyles } from '../../../utils/helpers';
import { PaymentFrequency, LegacyArchiveReason } from '../../../utils/constants';
import {
  advancedDatePickerOperator,
  advancedEnumOperators,
  advancedNumericOperators,
  advancedOperators,
  advancedBooleanOperators,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { getCommissionsInfoByType, UserInfo } from '../../../store/GeneralStore';

export const assetGridColumnDefs: GridCustomColDef[] = [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'termLength',
    headerName: 'Term Length',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    field: 'openCommissionsTicket',
    headerName: 'Open Ticket',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'ticketCount',
    headerName: 'Ticket Count',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'rpmAccountCount',
    headerName: 'RPM Account Count',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'split1SubAgent',
    headerName: 'Split 1 Sub-Agent',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'split1Rate',
    headerName: 'Split 1 Rate',
    sortable: true,
    filterable: false,
    width: 120,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'split2SubAgent',
    headerName: 'Split 2 Sub-Agent',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'split2Rate',
    headerName: 'Split 2 Rate',
    sortable: true,
    filterable: false,
    width: 120,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'x2Split',
    headerName: '2.0 Split',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'x2Rate',
    headerName: '2.0 Rate',
    sortable: true,
    filterable: false,
    width: 120,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'notesToCommissions',
    headerName: 'Notes to Commissions',
    sortable: true,
    filterable: true,
    width: 350,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'blendedRate',
    headerName: 'Is Blended Rate?',
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'firstRunBeforeSign',
    headerName: 'First Run Before Sign?',
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'firstRunBeforeParentDecommission',
    headerName: 'First Run Before Parent Decommission?',
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    sortable: true,
    filterable: true,
    width: 250,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'quoteRepInvolved',
    headerName: 'Direct Supplier Rep Involved in Deal?',
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'quoteTeamSellingApprovalNumber',
    headerName: 'Team Selling Approval Number',
    sortable: true,
    filterable: true,
    width: 350,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'quoteTeamSellingDiscount',
    headerName: 'Team Selling Discount %',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(PaymentFrequency ?? {}),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'paymentFrequency',
    headerName: 'Payment Frequency',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    hideByDefault: true,
    renderCell: (params: GridCellParams) => {
      const value = params.value as string;
      if (!value) return '';
      return (
        <Chip
          label={value}
          size="small"
          sx={chipStyles(value)}
          title={value}
        />
      );
    },
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'quoteType',
    headerName: 'Quote Type',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  }
];

interface AssetGridParams {
  showHiddenFields?: string[];
}

export const DefaultAssetGridColumnDefs = (params: AssetGridParams = {}): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.ASSET, sfId: params.row.sf_id, id: params.row.id, data: params.row })
        }}
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'number',
    headerName: 'Number',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/asset/${params.row.id}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    pinned: 'left',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'salesAgents'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'salesAgent',
    headerName: 'Sales Agent',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'directSales',
    headerName: 'Direct Sales',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'directSalesPaidPercent',
    headerName: 'Direct Sales %',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    ),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'suppliers'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'customer',
    headerName: 'Customer',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/customer/${params.row.accountGri}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionsStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Commissions Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'parentCommissionsStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Parent Commissions Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'serviceStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assetStatus',
    headerName: 'Service Status',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'tsd',
    headerName: 'TSD',
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'locationName',
    headerName: 'Location Name',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'locationAddress',
    headerName: 'Location Address',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedFirstCommissionRun',
    headerName: 'Exp. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualFirstCommissionRun',
    headerName: 'Act. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualFirstSpiffRun',
    headerName: 'Actual First SPIFF Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastCommissionRun',
    headerName: 'Last Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 150,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: !params.showHiddenFields?.includes('lastCommissionRun')
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'totalMRC',
    headerName: 'Total MRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionableMRC',
    headerName: 'Comm. MRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'grossMRR',
    headerName: 'Gross MRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'recurringCommissionPercent',
    headerName: 'Rec. Comm. %',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'contractSignDate',
    headerName: 'Contract Sign Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'latestSalesAssignmentDate',
    headerName: 'Latest Sales Assignment Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'installDate',
    headerName: 'Install Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'billingCommencementDate',
    headerName: 'Billing Commencement Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'disconnectDate',
    headerName: 'Disconnect Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'commissionsExperienceAnalyst',
    headerName: 'CEA',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column'
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'sf_id',
    headerName: 'SFID',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'id',
    headerName: 'ID',
    sortable: true,
    filterable: true,
    width: 300,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'accountId',
    headerName: 'Account Id',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'name',
    headerName: 'Name',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'supplierId',
    headerName: 'Supplier ID',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('user', 'cea').map((us) => (us as UserInfo).email),
    field: 'commissionsExperienceAnalystEmail',
    headerName: 'CEA Email',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'customerSuccessManager',
    headerName: 'CSM',
    sortable: true,
    filterable: false,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column'
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('user', 'csm').map((us) => (us as UserInfo).email),
    field: 'customerSuccessManagerEmail',
    headerName: 'CSM Email',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'circuitId',
    headerName: 'Circuit ID',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'commissionableNRC',
    headerName: 'Commissionable NRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    hideByDefault: true,
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'netNRR',
    headerName: 'Net NRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'netMRR',
    headerName: 'Net MRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'nonCommissionableNRC',
    headerName: 'Non Commissionable NRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'grossNRR',
    headerName: 'Gross NRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'contractNumber',
    headerName: 'Contract Number',
    sortable: true,
    filterable: true,
    hideByDefault: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'nonRecurringCommissionPercent',
    headerName: 'Non Recurring Commission %',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'actualDecommissionedDate',
    headerName: 'Actual Decommission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'estimatedDeliveryDate',
    headerName: 'Estimated Delivery Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'upcomingRenewalDate',
    headerName: 'Upcoming Renewal Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'cancelledDate',
    headerName: 'Cancelled Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'activationDate',
    headerName: 'Activation Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'expectedDecommissionDate',
    headerName: 'Expected Decommission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'commissionsValidatedDate',
    headerName: 'Commissions Validated Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'commissionsValidationStatus',
    headerName: 'Commissions Validation Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'commissionsValidationSubStatus',
    headerName: 'Commissions Validation Sub Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'legacyArchiveReason',
    headerName: 'Legacy Archive Reason',
    valueOptions: Object.values(LegacyArchiveReason ?? {}),
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'expectedRpmAccountNumber',
    headerName: 'Expected RPM Account Number',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'accountNumber',
    headerName: 'Account Number',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'ultimateParent',
    headerName: 'Ultimate Parent',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/customer/${params.row.ultimateParentId}`}>
        <div className="flex items-center text-small">{(params?.value as string) ?? ''}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'supplierOrderNumber',
    headerName: 'Supplier Order Number',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'tsdTrackingNumber',
    headerName: 'TSD Tracking Number',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true
  },
  ...assetGridColumnDefs
];
